import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Grid,
  Box,
  Typography,
  Divider,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { AppDispatch, RootState } from "../../stores";
import { getLeadStats } from "../../features/lead-module/leadAction";

interface ChangeIndicatorProps {
  change: string;
}

const StyledDashboardCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 2.5),
  borderRadius: 1.5,
  backgroundColor: "#f5f0e7",
  transition: "all 0.3s ease-in-out",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  "&:hover": {
    transform: "translateY(-2px)",
    backgroundColor: "#dbd7ce",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5, 2),
  },
  [theme.breakpoints.down("xs")]: {
    padding: theme.spacing(1, 1.5),
  },
  position: "relative",
}));

const ChangeIndicator = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "change",
})<ChangeIndicatorProps>(({ theme, change }) => ({
  marginTop: theme.spacing(2),
  fontWeight: 500,
  fontSize: "0.95rem",
  color: "#536060",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.75rem",
  },
}));

// Add the LoaderContainer and LoaderText styled components
const LoaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  width: "100%",
  minHeight: "120px",
}));

const LoaderText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  fontSize: "0.9rem",
  color: "#4b5b5d",
  fontWeight: 500,
}));

// Skeleton card for loading state
const SkeletonCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 2.5),
  borderRadius: 1.5,
  backgroundColor: "#f5f0e7",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  position: "relative",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5, 2),
  },
  [theme.breakpoints.down("xs")]: {
    padding: theme.spacing(1, 1.5),
  },
}));

const StatsCard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    leadStats,
    leadStatLoading: loading,
    leadStatError: error,
  } = useSelector((state: RootState) => state.lead);

  useEffect(() => {
    dispatch(getLeadStats());
  }, [dispatch]);

  const statsData = [
    {
      title: "Total Leads",
      value: leadStats?.totalLeads?.toString() || "0",
      change: "All leads over time",
    },
    {
      title: "New Leads",
      value: leadStats?.newLeads?.toString() || "0",
      change: "New leads added",
    },
    {
      title: "Conversion Rate",
      value: leadStats?.conversionRate || "0.00%",
      change: "Leads converted to customers",
    },
    {
      title: "Follow Ups",
      value: leadStats?.followUps?.toString() || "0",
      change: "Leads requiring follow-up",
    },
  ];

  if (loading) {
    return (
      <Grid
        container
        spacing={{ xs: 2, sm: 2, md: 3 }}
        sx={{ mb: { xs: 2, md: 4 } }}
      >
        {[1, 2, 3, 4].map((item) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={item}>
            <SkeletonCard>
              <LoaderContainer>
                <CircularProgress
                  size={32}
                  thickness={4}
                  sx={{
                    color: "#4b5b5d",
                  }}
                />
                <LoaderText>Loading stats...</LoaderText>
              </LoaderContainer>
            </SkeletonCard>
          </Grid>
        ))}
      </Grid>
    );
  }

  if (error) {
    return (
      <Box sx={{ my: 2, textAlign: "center" }}>
        <Typography color="error" sx={{ fontSize: "0.9rem", fontWeight: 500 }}>
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Grid
      container
      spacing={{ xs: 2, sm: 2, md: 3 }}
      sx={{ mb: { xs: 2, md: 4 } }}
    >
      {statsData.map((stat, index) => (
        <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
          <StyledDashboardCard>
            <Box
              sx={{
                position: "absolute",
                top: 15,
                right: 15,
                backgroundColor: "#4b5b5d",
                borderRadius: "50%",
                p: "2px 6px",
              }}
            >
              <ArrowOutwardIcon
                sx={{
                  color: "#dbd7ce",
                  fontSize: { xs: "1rem", sm: "1.2rem" },
                }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "row", sm: "row" },
                alignItems: "center",
                gap: { xs: 1.5, sm: 2 },
                flexGrow: 1,
              }}
            >
              <Box sx={{ flex: 1, overflow: "hidden" }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 500,
                    fontSize: { xs: "0.95rem", sm: "1rem", md: "1.1rem" },
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    color: "#536060",
                  }}
                >
                  {stat.title}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    mt: { xs: 0.5, sm: 1 },
                    mb: 0.5,
                    fontWeight: 700,
                    fontSize: { xs: "1.25rem", sm: "1.4rem", md: "1.6rem" },
                    color: "#536060",
                  }}
                >
                  {stat.value}
                </Typography>
                <Divider />
                <ChangeIndicator change={stat.change}>
                  {stat.change}
                </ChangeIndicator>
              </Box>
            </Box>
          </StyledDashboardCard>
        </Grid>
      ))}
    </Grid>
  );
};

export default StatsCard;
